import React from 'react'
import { supabase } from './supabase'
import { getSessionHeaders } from './serverSession'

export interface AdminUser {
 id: string
 email: string
 role: 'super_admin' | 'admin' | 'moderator'
 created_at: string
 updated_at: string
}

export interface AdminPermissions {
 canManageUsers: boolean
 canManagePayments: boolean
 canManageSponsors: boolean
 canManageGallery: boolean
 canViewAuditLogs: boolean
 canManageAdmins: boolean
 canAccessDebug: boolean
}

// Check if current user is admin
export const checkAdminStatus = async (userEmail: string): Promise<AdminUser | null> => {
 try {
 console.log('Checking admin status for:', userEmail)

 const { data, error } = await supabase
 .from('admin_users')
 .select('*')
 .eq('email', userEmail)
 .eq('is_active', true)
 .single()

 if (error) {
 console.log('Admin check failed:', error.code, error.message)
 return null
 }

 if (data) {
 console.log('Admin user verified:', data.email, data.role)
 return data as AdminUser
 }

 console.log('User is not an admin:', userEmail)
 return null
 } catch (err) {
 console.error('Error checking admin status:', err)
 return null
 }
}

// Get admin permissions via API (uses service role on server to bypass RLS)
export const getAdminPermissions = async (role: string): Promise<AdminPermissions> => {
 try {
 // Use API endpoint to fetch permissions - avoids RLS issues with client-side supabase
 const response = await fetch('/api/admin/roles', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ operation: 'get_role_permissions', data: { roleName: role } })
 })

 if (response.ok) {
 const result = await response.json()
 // API returns { success, data: { permissions: [...] } } or { success, permissions: [...] }
 const permissionKeys: string[] = result.data?.permissions || result.permissions || []

 return {
 canManageUsers: permissionKeys.includes('manage_users'),
 canManagePayments: permissionKeys.includes('manage_payments'),
 canManageSponsors: permissionKeys.includes('manage_sponsors'),
 canManageGallery: permissionKeys.includes('manage_gallery'),
 canViewAuditLogs: permissionKeys.includes('view_audit_logs'),
 canManageAdmins: permissionKeys.includes('manage_admins'),
 canAccessDebug: permissionKeys.includes('access_debug')
 }
 }

 console.error('Error fetching permissions via API:', response.statusText)
 return getDefaultPermissions()
 } catch (err) {
 console.error('Error getting admin permissions:', err)
 return getDefaultPermissions()
 }
}

// Fallback permissions (no access)
function getDefaultPermissions(): AdminPermissions {
 return {
 canManageUsers: false,
 canManagePayments: false,
 canManageSponsors: false,
 canManageGallery: false,
 canViewAuditLogs: false,
 canManageAdmins: false,
 canAccessDebug: false
 }
}

// Validate admin access for specific action
export const validateAdminAccess = async (
 userEmail: string,
 requiredPermission: keyof AdminPermissions
): Promise<boolean> => {
 try {
 const adminUser = await checkAdminStatus(userEmail)

 if (!adminUser) {
 return false
 }

 const permissions = await getAdminPermissions(adminUser.role)
 return permissions[requiredPermission]
 } catch (err) {
 console.error('Error validating admin access:', err)
 return false
 }
}

// Log admin action for audit trail
// Always uses API endpoint - NO getServiceRoleClient() in browser-reachable code
export const logAdminAction = async (
 adminEmail: string,
 action: string,
 targetType: string = 'system',
 targetId: string = 'N/A',
 details: any = {},
 oldValues: any = {},
 newValues: any = {}
) => {
 const isBrowser = typeof window !== 'undefined';
 const userAgent = isBrowser ? navigator.userAgent : 'server';

 try {
 console.log('Admin Action Logging (via API):', { adminEmail, action, targetType, targetId });

 const API_BASE_URL = isBrowser
 ? ((window as any).__VITE_API_BASE_URL__ || (import.meta as any)?.env?.VITE_API_BASE_URL || '')
 : '';

 const response = await fetch(`${API_BASE_URL}/api/admin/audit`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({
 operation: 'log_action',
 data: {
 adminEmail,
 action,
 targetType,
 targetId,
 details,
 oldValues,
 newValues,
 userAgent
 }
 })
 });

 if (!response.ok) {
 const result = await response.json();
 console.error('Failed to store audit log via API:', result.error);
 // Log to console as fallback
 console.log('AUDIT LOG (Console Fallback):', JSON.stringify({
 adminEmail, action, targetType, targetId, details, oldValues, newValues,
 timestamp: new Date().toISOString()
 }, null, 2));
 return false;
 }

 console.log('Audit log stored successfully via API');
 return true;
 } catch (err) {
 console.error('Error logging admin action:', err);
 // Log to console as fallback
 console.log('AUDIT LOG (Console Fallback):', JSON.stringify({
 adminEmail,
 action,
 targetType,
 targetId,
 details,
 oldValues,
 newValues,
 timestamp: new Date().toISOString()
 }, null, 2));
 return false;
 }
}

// React hook for admin authentication
export const useAdminAuth = () => {
 const [adminUser, setAdminUser] = React.useState<AdminUser | null>(null)
 const [permissions, setPermissions] = React.useState<AdminPermissions | null>(null)
 const [loading, setLoading] = React.useState(true)

 React.useEffect(() => {
 const checkAuth = async () => {
 try {
 // Get current user from Supabase auth
 const { data: { user } } = await supabase.auth.getUser()

 if (!user?.email) {
 setAdminUser(null)
 setPermissions(null)
 return
 }

 const admin = await checkAdminStatus(user.email)

 if (admin) {
 setAdminUser(admin)
 const perms = await getAdminPermissions(admin.role)
 setPermissions(perms)
 } else {
 setAdminUser(null)
 setPermissions(null)
 }
 } catch (err) {
 console.error('Error checking admin auth:', err)
 setAdminUser(null)
 setPermissions(null)
 } finally {
 setLoading(false)
 }
 }

 checkAuth()
 }, [])

 return {
 adminUser,
 permissions,
 loading,
 isAdmin: !!adminUser,
 isSuperAdmin: adminUser?.role === 'super_admin',
 hasPermission: (permission: keyof AdminPermissions) => 
 permissions ? permissions[permission] : false
 }
}

// Higher-order component for admin route protection
export const withAdminAuth = (
 WrappedComponent: React.ComponentType<any>,
 requiredPermission?: keyof AdminPermissions
) => {
 return function AdminProtectedComponent(props: any) {
 const { adminUser, permissions, loading, hasPermission } = useAdminAuth()

 if (loading) {
 return React.createElement('div',
 { className: "flex items-center justify-center py-12" },
 React.createElement('div', { className: "animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400" }),
 React.createElement('span', { className: "ml-3 text-gray-300" }, "Checking permissions...")
 )
 }

 if (!adminUser) {
 return React.createElement('div',
 { className: "text-center py-12" },
 React.createElement('div', { className: "text-red-400 text-6xl mb-4" }, ""),
 React.createElement('h2', { className: "text-2xl font-bold text-white mb-2" }, "Access Denied"),
 React.createElement('p', { className: "text-white" },
 "You need administrator privileges to access this section."
 )
 )
 }

 if (requiredPermission && !hasPermission(requiredPermission)) {
 return React.createElement('div',
 { className: "text-center py-12" },
 React.createElement('div', { className: "text-yellow-400 text-6xl mb-4" }, ""),
 React.createElement('h2', { className: "text-2xl font-bold text-white mb-2" }, "Insufficient Permissions"),
 React.createElement('p', { className: "text-white" },
 `Your admin role (${adminUser.role}) doesn't have permission to access this feature.`
 )
 )
 }

 return React.createElement(WrappedComponent, { ...props, adminUser, permissions })
 }
}

// Admin action wrapper with logging
export const withAdminLogging = (
 action: string,
 targetType: string
) => {
 return function (_target: any, _propertyName: string, descriptor: PropertyDescriptor) {
 const method = descriptor.value

 descriptor.value = async function (...args: any[]) {
 try {
 // Get admin user email (would be from context/props)
 const adminEmail = 'current_admin@example.com' // TODO: Get from context
 
 // Execute the original method
 const result = await method.apply(this, args)
 
 // Log the action
 await logAdminAction(
 adminEmail,
 action,
 targetType,
 args[0]?.id || 'unknown',
 { args: args.slice(1) }
 )
 
 return result
 } catch (err) {
 console.error(`Error in admin action ${action}:`, err)
 throw err
 }
 }
 }
}
